/**
 * Poll one study's ingest state cheaply, and tell the caller when the full
 * study payload is actually worth re-fetching.
 *
 * WHY THIS REPLACES POLLING THE STUDY ITSELF
 * ------------------------------------------
 * The viewer used to poll `GetStudyById` every 10s while a study was arriving.
 * That endpoint signs TWO CDN URLs per image (object + thumbnail), so an
 * 800-slice CT cost ~1,600 HMAC signings and a few hundred KB of response on
 * every tick, for every open viewer. At peak — thousands of viewers on
 * receiving studies — that is a large amount of CPU and egress spent on a
 * payload that, in the overwhelmingly common case, is byte-for-byte the same
 * study it was ten seconds ago. Worse, the viewer's identity-preserving merges
 * deliberately discard the freshly-signed URLs to protect cache identity, so
 * most of that work was thrown away on arrival.
 *
 * So: poll a few counts, and only pull the real payload when a count moves.
 *
 * WHAT COUNTS AS "MOVED"
 * Series count, image count, or pending count. Pending is included because it
 * changes when pixels land for images we already knew about — the study did not
 * grow, but images that were unloadable have become loadable, and the viewer
 * needs the refreshed payload to get their URLs.
 *
 * MOVED SINCE WHEN
 * Since the counts the CALLER holds — not since the first reading this hook
 * happened to witness. The viewer's study queries are cache-first and no longer
 * poll on the happy path, so a study opened mid-import, navigated away from, and
 * re-opened inside the cache window renders whatever it was cached with. A hook
 * that only reports deltas it personally observed is blind to exactly that
 * window: the import finished while nothing was watching, the first reading
 * after the remount gets swallowed as a fresh baseline, and the new series never
 * appear. So callers hand over their own counts and the first reading is
 * reconciled against those. See {@link IngestBaselineCounts}.
 */
import { getStudyIngestStatus } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
/** Poll cadence while the study is still arriving. */
const INGEST_STATUS_ACTIVE_MS = 10_000;
/**
 * Poll cadence once nothing is pending. Still non-zero: a modality can start a
 * new series after a pause, and this poll is cheap enough that staying subscribed
 * costs far less than the full-payload poll it replaced.
 */
const INGEST_STATUS_IDLE_MS = 60_000;
function signature(s) {
    return `${s.seriesCount}:${s.imageCount}:${s.pendingImageCount}`;
}
/** True when the server's reading contradicts what the caller is holding. */
function disagreesWithBaseline(baseline, status) {
    return baseline.seriesCount !== status.seriesCount || baseline.imageCount !== status.imageCount;
}
export function useStudyIngestStatus(studyId, enabled = true, callerCounts) {
    const query = useQuery({
        queryKey: ['study-ingest-status', studyId],
        queryFn: () => getStudyIngestStatus(studyId),
        enabled: Boolean(studyId) && enabled,
        // Adapt to what the study is doing: fast while pixels are outstanding,
        // slow once storage has caught up.
        refetchInterval: (q) => q.state.data?.isReceiving ? INGEST_STATUS_ACTIVE_MS : INGEST_STATUS_IDLE_MS,
        // Deliberately false, matching the study query: a hidden tab is not being
        // read, and waking N background windows to poll is exactly the churn this
        // hook exists to remove.
        refetchIntervalInBackground: false,
        // The counts ARE the payload; there is nothing to go stale into.
        staleTime: 0,
    });
    const status = query.data;
    const [revision, setRevision] = useState(0);
    const lastSignatureRef = useRef(null);
    const callerCountsRef = useRef(callerCounts);
    // DECLARATION ORDER IS LOAD-BEARING across the three effects below: React runs
    // a component's effects in the order its hooks were called, and within one
    // commit the baseline has to be cleared, then refreshed, before a reading is
    // judged against it.
    // Reset when the subject changes, or the first reading for study B would be
    // compared against study A's counts and spuriously signal a change. Ahead of
    // the comparison because study B may already have a CACHED ingest reading, in
    // which case `status` is B's data on the very commit the id changes.
    useEffect(() => {
        lastSignatureRef.current = null;
    }, [studyId]);
    // Keep the caller's counts current WITHOUT making them a dependency of the
    // comparison: the caller derives them from its own payload and hands over a
    // fresh object every render, and only a new server reading may move
    // `revision`.
    useEffect(() => {
        callerCountsRef.current = callerCounts;
    });
    useEffect(() => {
        if (!status)
            return;
        const next = signature(status);
        if (lastSignatureRef.current === null) {
            lastSignatureRef.current = next;
            // First reading after a (re)mount. Reconcile it against what the caller
            // actually holds: a disagreement means the study moved while nothing was
            // watching, and that change still has to be published. With nothing
            // cached there is no stale payload to correct, so the reading is simply
            // the baseline.
            const baseline = callerCountsRef.current;
            if (baseline && disagreesWithBaseline(baseline, status))
                setRevision((r) => r + 1);
            return;
        }
        if (lastSignatureRef.current !== next) {
            lastSignatureRef.current = next;
            setRevision((r) => r + 1);
        }
    }, [status]);
    return {
        isReceiving: status?.isReceiving ?? false,
        pendingImageCount: status?.pendingImageCount ?? 0,
        revision,
        status,
    };
}
